import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  AvatarGroup,
  Tooltip,
  Popover,
  Typography,
  Box,
} from "@mui/material";
import axios from "axios";
import { getAvatarByGender } from "../../utils/avatarUtils";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ViewQuestionnaireModal from "./ViewQuestionnaireModal";
import AssignQuestionnaireModal from "./AssignQuestionnaireModal";

interface Questionnaire {
  id: number;
  name: string;
  assignedTo: Array<{ id: string; gender: string }>;
  questions: Array<{
    id: number;
    text: string;
    question_type: string;
    possible_answers?: string;
  }>;
}

const MAX_AVATARS = 3;

const QuestionnaireList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<Questionnaire | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState<
    Array<{ id: string; gender: string }>
  >([]);

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/questionnaires`
      );
      setQuestionnaires(response.data);
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (questionnaire: Questionnaire) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/questionnaires/${questionnaire.id}/questions`
      );
      const updatedQuestionnaire = {
        ...questionnaire,
        questions: response.data,
      };
      setSelectedQuestionnaire(updatedQuestionnaire);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error("Error fetching questionnaire questions:", error);
    }
  };

  const handleAssign = (questionnaire: Questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setIsAssignModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedQuestionnaire(null);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedQuestionnaire(null);
  };

  const handleAssignSubmit = async (memberId: string) => {
    if (selectedQuestionnaire) {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/questionnaires/${selectedQuestionnaire.id}/assign`,
          { memberId }
        );
        fetchQuestionnaires();
        handleCloseAssignModal();
      } catch (error) {
        console.error("Error assigning questionnaire:", error);
      }
    }
  };

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    assignedTo: Array<{ id: string; gender: string }>
  ) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(assignedTo);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questionnaires.map((questionnaire) => (
              <TableRow key={questionnaire.id}>
                <TableCell>{questionnaire.name}</TableCell>
                <TableCell>
                  <AvatarGroup max={MAX_AVATARS}>
                    {questionnaire.assignedTo
                      .slice(0, MAX_AVATARS)
                      .map((member, index) => (
                        <Avatar
                          key={index}
                          src={getAvatarByGender(member.gender)}
                        />
                      ))}
                  </AvatarGroup>
                  {questionnaire.assignedTo.length > MAX_AVATARS && (
                    <Button
                      size="small"
                      onClick={(e) =>
                        handlePopoverOpen(e, questionnaire.assignedTo)
                      }
                    >
                      +{questionnaire.assignedTo.length - MAX_AVATARS} more
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleView(questionnaire)}
                    sx={{ mr: 1 }}
                  >
                    View
                  </Button>
                  <Button onClick={() => handleAssign(questionnaire)}>
                    Assign
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ViewQuestionnaireModal
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        questionnaire={selectedQuestionnaire}
      />
      <AssignQuestionnaireModal
        open={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        questionnaireId={selectedQuestionnaire?.id || 0}
        onAssign={handleAssignSubmit}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Assigned Participants
          </Typography>
          {popoverContent.map((member, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 1 }}
            >
              <Avatar sx={{ mr: 1 }} src={getAvatarByGender(member.gender)} />
              <Typography>{member.id}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default QuestionnaireList;
